'use client'
import { createContext, useEffect, useMemo, useRef, useState } from 'react'
import { useChatbotIndexedDb } from '../hooks/useChatbotIndexedDb'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useSession } from 'session/src/hooks/useSession'
import {
	ABORT_REASON,
	CHATBOT_SESSION_ID_LS_ID,
	CHAT_MESSAGE_CHOICES,
	IS_MESSAGE_PULLING_LS_ID,
} from '../constants/constants'

import replacePlaceholders from 'labels/lib/replacePlaceholders'

import { sendTalkChatbotEvent } from '../analytics/sendTalkChatbotEvernt'
import { placeholderResponses } from '../constants/placeholderResponses'
import { INTERVAL_AGENT_PULLING_TIME } from '../constants/timeout'
import { useChatBotInit } from '../hooks/useChatBotInit'
import { useChatBotResponse } from '../hooks/useChatBotResponse'
import { useChatbotMessagePulling } from '../hooks/useChatbotMessagePulling'
import { useChatbotTimeout } from '../hooks/useChatbotTimeout'
import { ConversationTrigger } from '../types/AIResponseTypes'
import type {
	ChatMessagesContext,
	GenerateAIResponseProps,
	SendHumanMessageParams,
} from '../types/ChatMessagesContextTypes'
import {
	type ChatbotMessage,
	MessageRole,
	MessageType,
	type QuestionMessage,
} from '../types/messageTypes'
import { createAITextMessage } from '../utils/createAITextMessage'
import { getHasChoiceMessage } from '../utils/getHasChoiceMessage'
import { getHasHumanInteraction } from '../utils/getHasHumanInteraction'
import { getHumanMessagesCount } from '../utils/getHumanMessagesCount'
import { getInitChatInfo } from '../utils/getInitChatInfo'
import {
	getAIResponseMessages,
	getAgentResponseMessages,
} from '../utils/getResponseMessages'
import { regenerateFA } from '../utils/regenerateFa'

export const ChatbotMessagesContext = createContext<
	ChatMessagesContext | undefined
>(undefined)

interface ChatbotMessagesProviderProps {
	readonly children: React.ReactNode
}

export function ChatbotMessagesProvider({
	children,
}: ChatbotMessagesProviderProps): React.ReactNode {
	const { t } = useLabels()
	const { name } = useSession()

	const [chatHistoryMessages, setChatHistoryMessages] = useState<
		ChatbotMessage[]
	>([])
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [shouldFadeoutMessages, setShouldFadeoutMessages] =
		useState<boolean>(false)

	const isAgentConversationActiveRef = useRef<boolean>(false)
	const chatbotSessionIdRef = useRef<string>('')
	const pullingIntervalIDRef = useRef<ReturnType<typeof setTimeout>>(undefined)
	const messagesToSendAfterFadeoutRef = useRef<{
		message: QuestionMessage
		responseMessageType?: MessageType.AboutAI
	} | null>(null)

	const { triggerChatBotInit } = useChatBotInit()
	const { triggerAIResponse, abortAIRequest } = useChatBotResponse()
	const { triggerChatbotMessagePulling } = useChatbotMessagePulling()
	const { saveData, getData, openDatabase, clearData } = useChatbotIndexedDb()
	const {
		clearAgentInterval,
		clearChatInteractionInterval,
		initAgentResponseTimeout,
		initChatInteractionInterval,
	} = useChatbotTimeout({
		generateAIResponse,
		addMessagesHistory,
		isAgentConversationActiveRef,
		clearPullingInterval: () => clearInterval(pullingIntervalIDRef.current),
	})

	const hasHumanInteraction = getHasHumanInteraction(chatHistoryMessages)

	function deleteChoices(deleteChoicesProps?: {
		conversationTrigger: ConversationTrigger
	}) {
		setChatHistoryMessages((prevMessages) =>
			prevMessages.filter((prevMessage) => {
				const type = prevMessage.type
				return !CHAT_MESSAGE_CHOICES.includes(type)
			})
		)
		setShouldFadeoutMessages(false)

		if (messagesToSendAfterFadeoutRef.current) {
			const { message, responseMessageType } =
				messagesToSendAfterFadeoutRef.current
			const conversationTrigger = deleteChoicesProps?.conversationTrigger
			addMessagesHistory([message])
			generateAIResponse({
				message: message.value,
				responseMessageType,
				conversationTrigger,
			})
			messagesToSendAfterFadeoutRef.current = null

			if (conversationTrigger === ConversationTrigger.EndConversation) {
				clearInterval(pullingIntervalIDRef.current)
				clearAgentInterval()
				setIsLoading(false)
				addMessagesHistory([
					createAITextMessage({
						textMessage: t('assistant.handoff.queue.left.text'),
					}),
				])
			}
		}
	}

	function addMessagesHistory(messages: ChatbotMessage[]) {
		setChatHistoryMessages((prevMessages) => [...prevMessages, ...messages])
	}

	function startPullingAgent() {
		setIsLoading(true)
		clearInterval(pullingIntervalIDRef.current)

		pullingIntervalIDRef.current = setInterval(async () => {
			const response = await triggerChatbotMessagePulling({
				chatbotSessionId: chatbotSessionIdRef.current,
			})
			// agentAvailable can be undefined with an active agent
			const hasAgentUnavailable = response.info.agentAvailable === false
			const isLastAgentMessage =
				response?.info?.action === 'end' && !hasAgentUnavailable
			const isFirstAgentMessage = response.info.action === 'established'

			const newMessages = getAgentResponseMessages({
				response,
				isFirstAgentMessage,
				isLastAgentMessage,
			})

			if (isFirstAgentMessage) {
				isAgentConversationActiveRef.current = true
				initAgentResponseTimeout()
			}
			if (newMessages) {
				addMessagesHistory(newMessages)
				setIsLoading(false)
				sendTalkChatbotEvent({
					id: getHumanMessagesCount(chatHistoryMessages),
					isFashionAssistantRegenerate: false,
					isAgent: true,
				})
			}
			if (response.info.action === 'end') {
				isAgentConversationActiveRef.current = false
				localStorage.setItem(IS_MESSAGE_PULLING_LS_ID, 'false')
				clearInterval(pullingIntervalIDRef.current)
				clearAgentInterval()
			}
		}, INTERVAL_AGENT_PULLING_TIME)
	}

	async function generateAIResponse({
		message,
		responseMessageType,
		conversationTrigger,
	}: GenerateAIResponseProps) {
		if (responseMessageType) {
			const placeholderResponse = placeholderResponses[responseMessageType]
			addMessagesHistory([placeholderResponse])
			return
		}
		try {
			setIsLoading(true)
			const response = await triggerAIResponse({
				chatbotSessionId: chatbotSessionIdRef.current,
				message,
				conversationTrigger,
			})

			const isTimeout =
				conversationTrigger === ConversationTrigger.TimeoutConversation
			const isConversationEnd =
				conversationTrigger === ConversationTrigger.EndConversation
			const isFashionAssistantRegenerate =
				conversationTrigger === ConversationTrigger.RegenerateSearch

			const newMessages = getAIResponseMessages({
				response,
				isTimeoutAgentMessage:
					isTimeout && isAgentConversationActiveRef.current,
			})

			if (newMessages) {
				setIsLoading(false)
				if (!isTimeout) {
					addMessagesHistory(newMessages)
				}

				sendTalkChatbotEvent({
					isFashionAssistantRegenerate,
					id: getHumanMessagesCount(chatHistoryMessages),
					responseMessages: response.queryResult.responseMessages,
				})
			}

			if (
				!isAgentConversationActiveRef.current &&
				response.info.operator &&
				!isConversationEnd
			) {
				startPullingAgent()
				localStorage.setItem(IS_MESSAGE_PULLING_LS_ID, 'true')
			}

			if (isTimeout || isConversationEnd) {
				const newToken = crypto.randomUUID()
				localStorage.setItem(CHATBOT_SESSION_ID_LS_ID, newToken)
				chatbotSessionIdRef.current = newToken
				isAgentConversationActiveRef.current = false
			}
		} catch (error) {
			if (error !== ABORT_REASON) {
				addMessagesHistory([
					createAITextMessage({
						textMessage: t('assistant.general.error'),
					}),
				])
				console.error(error)
				setIsLoading(false)
			}
		}
	}

	function sendHumanMessage({
		messageContent,
		responseMessageType,
	}: SendHumanMessageParams) {
		initChatInteractionInterval()
		const newMessage: QuestionMessage = {
			value: messageContent,
			role: MessageRole.Human,
			type: MessageType.Question,
			showAvatar: true,
		}
		const shouldFadeoutMessagesFirst = getHasChoiceMessage(chatHistoryMessages)

		if (isAgentConversationActiveRef.current) {
			initAgentResponseTimeout()
		}

		if (shouldFadeoutMessagesFirst) {
			messagesToSendAfterFadeoutRef.current = {
				responseMessageType,
				message: newMessage,
			}

			setShouldFadeoutMessages(true)
		} else {
			addMessagesHistory([newMessage])
			generateAIResponse({
				message: messageContent,
				responseMessageType,
			})
		}
	}

	function clearMessages() {
		abortAIRequest?.()

		if (pullingIntervalIDRef.current) {
			clearInterval(pullingIntervalIDRef.current)
			generateAIResponse({
				conversationTrigger: ConversationTrigger.EndConversation,
			})
			isAgentConversationActiveRef.current = false
		}
		if (localStorage.getItem(IS_MESSAGE_PULLING_LS_ID)) {
			localStorage.setItem(IS_MESSAGE_PULLING_LS_ID, 'false')
		}

		clearAgentInterval()
		clearChatInteractionInterval()
		localStorage.removeItem(CHATBOT_SESSION_ID_LS_ID)
		setIsLoading(false)
		setChatHistoryMessages([])
		clearData()
	}

	function initMessagePulling() {
		startPullingAgent()
		initAgentResponseTimeout()
		setIsLoading(false)
	}

	function initChatBot() {
		initChatInteractionInterval()

		openDatabase(async () => {
			const { token, iceBreakers } = await getInitChatInfo(triggerChatBotInit)

			chatbotSessionIdRef.current = token
			localStorage.setItem(CHATBOT_SESSION_ID_LS_ID, token)

			if (localStorage.getItem(IS_MESSAGE_PULLING_LS_ID) === 'true') {
				initMessagePulling()
			}

			getData((storedMessages) => {
				setChatHistoryMessages(storedMessages || [])

				if (iceBreakers && !storedMessages) {
					const titleLabel = name
						? replacePlaceholders(t('assistant.helloName.text'), { 1: name })
						: t('assistant.hello.text')

					const greetingsMessage = createAITextMessage({
						textMessage: titleLabel,
					})
					addMessagesHistory([greetingsMessage, iceBreakers])
				}
			})
		})
	}

	useEffect(() => {
		if (chatHistoryMessages.length > 0) {
			saveData(chatHistoryMessages)
		}
	}, [chatHistoryMessages])

	const value = useMemo(
		() => ({
			sendHumanMessage,
			chatHistoryMessages,
			initChatBot,
			hasHumanInteraction,
			clearMessages,
			isLoading,
			shouldFadeoutMessages,
			deleteChoices,
			regenerateFAResponse: () =>
				regenerateFA({
					setChatHistoryMessages,
					generateAIResponse,
				}),
		}),
		[
			sendHumanMessage,
			chatHistoryMessages,
			initChatBot,
			hasHumanInteraction,
			clearMessages,
			isLoading,
			shouldFadeoutMessages,
		]
	)

	return (
		<ChatbotMessagesContext.Provider value={value}>
			{children}
		</ChatbotMessagesContext.Provider>
	)
}
